import React, { useState, useMemo, useCallback, useEffect } from "react";
import { AgGridReact } from "ag-grid-react";
import moment from "moment";
import { Constants } from "@eagerdog/constants";
import { getColumns } from "../../../utils/ScoresGrid/getColumns";
import { getDataRows } from "../../../utils/ScoresGrid/getDataRows";
import Dropdown from "../../../components/Dropdown/Dropdown";
import Button from "../../../components/Button/Button";
import styles from "./DetailedResultsTable.module.scss";

interface IProps {
  scores: any[];
  event: any;
  shows: any[];
}

const DetailedResultsTable: React.FC<IProps> = ({ scores, event, shows }) => {
  const [selectedShow, setSelectedShow] = useState<any>({ id: "all-shows", value: "All Shows", label: "All Shows", data: null });
  const [selectedElement, setSelectedElement] = useState<any>({ id: "all", value: "All", label: "All" });
  const [selectedLevel, setSelectedLevel] = useState<any>({ id: "all", value: "All", label: "All" });

  // Build show options
  const showOptions = useMemo(() => {
    const options = [{ id: "all-shows", value: "All Shows", label: "All Shows", data: null }];
    shows.forEach((show) => {
      const timezone = event.timezone || moment.tz.guess();
      const showDate = moment(show.show_date).tz(timezone);
      const showType = (Constants.show_type as any)[show.show_type] || show.show_type;
      const formattedLabel = `${showType} ${show.show_name} (${showDate.format("ddd, h:mm a")})`;
      
      options.push({
        id: show._id, // Use _id as the identifier for filtering
        value: formattedLabel, // Display formatted label
        label: formattedLabel, // Display formatted label
        data: show, // Store the full show object
      });
    });
    return options;
  }, [shows, event]);

  // Build element options based on selected show
  const elementOptions = useMemo(() => {
    const elements = new Set<string>();
    elements.add("All");

    const filteredScores = selectedShow.id === "all-shows" 
      ? scores 
      : scores.filter((s) => s.show_id === selectedShow.id);

    filteredScores.forEach((score) => {
      const element = score.dog_class?.element || score.element;
      if (element) elements.add(element);
    });

    return Array.from(elements).map((e) => ({ id: e, value: e, label: e }));
  }, [scores, selectedShow]);

  // Build level options based on selected show and element
  const levelOptions = useMemo(() => {
    const levels = new Set<string>();
    levels.add("All");

    let filteredScores = selectedShow.id === "all-shows" 
      ? scores 
      : scores.filter((s) => s.show_id === selectedShow.id);

    if (selectedElement.value !== "All") {
      filteredScores = filteredScores.filter(
        (s) => (s.dog_class?.element || s.element) === selectedElement.value
      );
    }

    filteredScores.forEach((score) => {
      const level = score.dog_class?.level;
      if (level) levels.add(level);
    });

    return Array.from(levels).map((l) => ({ id: l, value: l, label: l }));
  }, [scores, selectedShow, selectedElement]);

  // Ensure selected values remain valid when options change
  useEffect(() => {
    const elementExists = elementOptions.some(opt => opt.value === selectedElement.value);
    if (!elementExists) {
      setSelectedElement({ id: "all", value: "All", label: "All" });
    }
  }, [elementOptions, selectedElement.value]);

  useEffect(() => {
    const levelExists = levelOptions.some(opt => opt.value === selectedLevel.value);
    if (!levelExists) {
      setSelectedLevel({ id: "all", value: "All", label: "All" });
    }
  }, [levelOptions, selectedLevel.value]);

  // Filter scores based on selections
  const filteredScores = useMemo(() => {
    let filtered = [...scores];

    if (selectedShow.id !== "all-shows") {
      filtered = filtered.filter((s) => s.show_id === selectedShow.id);
    }

    if (selectedElement.value !== "All") {
      filtered = filtered.filter(
        (s) => (s.dog_class?.element || s.element) === selectedElement.value
      );
    }

    if (selectedLevel.value !== "All") {
      filtered = filtered.filter((s) => s.dog_class?.level === selectedLevel.value);
    }

    return filtered;
  }, [scores, selectedShow, selectedElement, selectedLevel]);

  // Get columns and data rows using existing utilities
  const columns = useMemo(() => {
    return getColumns(
      selectedShow.id === "all-shows" ? { value: "None", label: "None" } : selectedShow,
      event,
      shows,
      undefined, // no fetchData function needed for read-only view
      [], // empty run-offs array
      true // isUserView = true (read-only)
    );
  }, [selectedShow, event, shows]);

  const rowData = useMemo(() => {
    return getDataRows(filteredScores, event, []);
  }, [filteredScores, event]);

  const handleReset = useCallback(() => {
    setSelectedShow({ id: "all-shows", value: "All Shows", label: "All Shows", data: null });
    setSelectedElement({ id: "all", value: "All", label: "All" });
    setSelectedLevel({ id: "all", value: "All", label: "All" });
  }, []);

  return (
    <div className={styles.DetailedResultsTable}>
      <div className={styles.header}>
        <h2 className={styles.sectionTitle}>Detailed Results</h2>
      </div>

      <div className={styles.content}>
        <div className={styles.filters}>
          <Dropdown
            label="Show"
            value={selectedShow}
            onChange={(_e: any, value: any) => setSelectedShow(value)}
            options={showOptions}
            placeholder="Select Show"
            className={styles.filterDropdown}
          />
          
          <Dropdown
            label="Element"
            value={selectedElement}
            onChange={(_e: any, value: any) => setSelectedElement(value)}
            options={elementOptions}
            placeholder="Select Element"
            className={styles.filterDropdown}
          />
          
          <Dropdown
            label="Level"
            value={selectedLevel}
            onChange={(_e: any, value: any) => setSelectedLevel(value)}
            options={levelOptions}
            placeholder="Select Level"
            className={styles.filterDropdown}
          />

          <Button
            variant="secondary"
            onClick={handleReset}
            className={styles.resetButton}
          >
            Reset
          </Button>
        </div>

        <div className={styles.tableWrap}>
          <div className={styles.tableWrapInner}>
            <AgGridReact
              rowData={rowData}
              columnDefs={columns}
              domLayout="autoHeight"
              gridOptions={{
                defaultColDef: {
                  flex: 1,
                  minWidth: 100,
                  resizable: true
                },
                animateRows: true,
                rowHeight: 48,
                headerHeight: 48,
                suppressCellFocus: true,
                pagination: true,
                paginationPageSize: 10,
                paginationPageSizeSelector: [10, 25, 50]
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default DetailedResultsTable;

